import { useCallback, useEffect, useMemo, useReducer } from "react"
import { useLocation, useNavigate } from "react-router"

import {
  useAccountAnalytics,
  useBuyerAnalytics,
  type AccountAnalyticsRow,
  type AnalyticsTotals,
  type BuyerAnalyticsRow,
} from "@/api/hooks/dashboard"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { NavbarTabList, navbarTabClass } from "@/components/NavbarTabs"
import { useNavbarLeftAction, usePageTitle } from "@/layouts/pageChrome"

import {
  DASHBOARD_ACCOUNT_COLUMN_STORAGE_KEY,
  DASHBOARD_ACCOUNT_COLUMNS,
  DASHBOARD_BUYER_COLUMN_STORAGE_KEY,
  DASHBOARD_BUYER_COLUMNS,
  saveColumnOrder,
} from "./lib/columns"
import {
  _computeTotalsFromAccountRows,
  _computeTotalsFromBuyerRows,
} from "./lib/totals"
import { runPreset, type DashboardDatePreset } from "./lib/presets"
import {
  resolveAccountStatusParam,
  resolveBuyerStatusParam,
} from "./lib/statusFilters"
import {
  createInitialDashboardState,
  dashboardReducer,
} from "./state/dashboardReducer"
import { DashboardFilters } from "./widgets/DashboardFilters"
import { BuyerAnalyticsTable } from "./widgets/BuyerAnalyticsTable"
import { AccountAnalyticsTable } from "./widgets/AccountAnalyticsTable"
import { Pagination } from "@/components/Pagination"

const DASHBOARD_PAGE_SIZE = 25

/**
 * React port of `templates/dashboard.html` (T3.3). Parity checklist:
 * `docs/migration/parity/dashboard.md`.
 *
 * Major divergences from the Jinja source:
 *
 *   1. Two queries are always registered, one per tab. The inactive
 *      tab's query is disabled via TanStack's `enabled` so only the
 *      visible tab fetches. Mirrors the legacy "swap visible section,
 *      refetch" flow without juggling `setTimeout`s.
 *   2. `/auth/me` is NOT refetched here — the admin gate + username
 *      are consumed from `useAuth()` via the outer AppShell.
 *      (Parity open question #13 resolution: consume, don't refetch.)
 *   3. Filter persistence to URL query params is intentionally NOT
 *      added — the parity file flagged it as an open question, and
 *      the Jinja source never persisted filters. Match source.
 *   4. Column reorder persistence uses `localStorage` under the
 *      same keys as the Jinja source (`dashboardBuyerColumnOrder` /
 *      `dashboardAccountColumnOrder`) so users upgrading keep their
 *      hand-rolled layout.
 *   5. The ads-manager link cells build `/adsmanager?...` URLs.
 *   6. Emojis in UI copy (🔄 refresh, ▲/▼ sort indicators, ✅
 *      success banner, ‹‹/‹/›/›› calendar nav) are preserved literally
 *      from the source — parity rule, not a code-style violation.
 */

export default function DashboardPage() {
  usePageTitle("", "Dashboard")
  const [state, dispatch] = useReducer(
    dashboardReducer,
    undefined,
    createInitialDashboardState,
  )

  const buildTikTokManageLink = useCallback(
    (advertiserId: string) => {
      const params = new URLSearchParams()
      params.set("aadvid", String(advertiserId))
      if (state.startDate) params.set("st", state.startDate)
      if (state.endDate) params.set("et", state.endDate)
      return `https://ads.tiktok.com/i18n/manage/campaign?${params.toString()}`
    },
    [state.startDate, state.endDate],
  )

  const buyerQueryParams = useMemo(
    () => ({
      start_date: state.startDate,
      end_date: state.endDate,
      network:
        state.selectedNetworks.length === 1 ? state.selectedNetworks[0] : "all",
      status: resolveBuyerStatusParam(state.selectedBuyerStatuses),
      user_ids: state.selectedBuyers,
    }),
    [
      state.startDate,
      state.endDate,
      state.selectedNetworks,
      state.selectedBuyerStatuses,
      state.selectedBuyers,
    ],
  )

  const accountQueryParams = useMemo(
    () => ({
      start_date: state.startDate,
      end_date: state.endDate,
      network:
        state.selectedNetworks.length === 1 ? state.selectedNetworks[0] : "all",
      status: resolveAccountStatusParam(state.selectedAccountStatuses),
      user_ids: state.selectedBuyers,
    }),
    [
      state.startDate,
      state.endDate,
      state.selectedNetworks,
      state.selectedAccountStatuses,
      state.selectedBuyers,
    ],
  )

  const buyerQuery = useBuyerAnalytics(buyerQueryParams)
  const accountQuery = useAccountAnalytics(accountQueryParams, {
    enabled: state.activeTab === "account",
  })

  // Success toast: show once when ?tiktok_connected=true is on the
  // URL, then strip the param so refresh doesn't re-show it.
  const location = useLocation()
  const navigate = useNavigate()
  const tiktokConnected = useMemo(
    () => new URLSearchParams(location.search).get("tiktok_connected") === "true",
    [location.search],
  )
  useEffect(() => {
    if (tiktokConnected) {
      navigate(location.pathname, { replace: true })
    }
    // Only fire once on mount when the param is present — the
    // `navigate` call will re-render without the query string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Resolve error banner text: API `message` → fetch error →
  // fallback. Parity: dashboard.html:2164-2166 / 2180-2183.
  const activeQuery = state.activeTab === "buyer" ? buyerQuery : accountQuery
  const errorMessage = useMemo(() => {
    if (activeQuery.isError) {
      const err = activeQuery.error
      return err instanceof Error
        ? err.message
        : "Failed to load dashboard analytics"
    }
    if (
      activeQuery.data &&
      typeof activeQuery.data === "object" &&
      "success" in activeQuery.data &&
      activeQuery.data.success === false
    ) {
      return (
        (activeQuery.data as { message?: string }).message ||
        "Failed to load dashboard analytics"
      )
    }
    return null
  }, [activeQuery.isError, activeQuery.error, activeQuery.data])

  // --------- Buyer view -------------------------------------------------
  const buyerRows: BuyerAnalyticsRow[] = useMemo(
    () => buyerQuery.data?.data ?? [],
    [buyerQuery.data],
  )
  const buyerApiTotals: AnalyticsTotals | null = useMemo(() => {
    const raw = buyerQuery.data?.totals
    if (!raw || typeof raw !== "object") return null
    if (Object.keys(raw).length === 0) return null
    return raw as AnalyticsTotals
  }, [buyerQuery.data])

  const buyerOptions = useMemo(
    () =>
      buyerRows.map((r) => ({ id: r.user_id, label: r.username || String(r.user_id) })),
    [buyerRows],
  )

  const buyerFiltered = useMemo(() => {
    let rows = buyerRows
    if (state.selectedBuyers.length > 0) {
      const set = new Set(state.selectedBuyers)
      rows = rows.filter((r) => set.has(r.user_id))
    }
    const q = state.search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const name = (r.username || "").toLowerCase()
      const role = (r.role || "").toLowerCase()
      return name.includes(q) || role.includes(q)
    })
  }, [buyerRows, state.search, state.selectedBuyers])

  const buyerSorted = useMemo(
    () =>
      sortRows(
        buyerFiltered,
        state.buyerSortKey,
        state.buyerSortDir,
        DASHBOARD_BUYER_COLUMNS.find((c) => c.id === state.buyerSortKey)
          ?.type ?? "number",
      ),
    [buyerFiltered, state.buyerSortKey, state.buyerSortDir],
  )

  const buyerPageRows = useMemo(() => {
    const start = (state.buyerPage - 1) * DASHBOARD_PAGE_SIZE
    return buyerSorted.slice(start, start + DASHBOARD_PAGE_SIZE)
  }, [buyerSorted, state.buyerPage])

  const buyerTotals: AnalyticsTotals | null = useMemo(() => {
    if (state.search.trim() || state.selectedBuyers.length > 0)
      return _computeTotalsFromBuyerRows(buyerFiltered)
    return buyerApiTotals
  }, [state.search, state.selectedBuyers, buyerFiltered, buyerApiTotals])

  // --------- Account view -----------------------------------------------
  const accountRows: AccountAnalyticsRow[] = useMemo(
    () =>
      (accountQuery.data?.data ?? []).map((r) => ({
        ...r,
        tiktok_url: buildTikTokManageLink(r.advertiser_id),
      })),
    [accountQuery.data, buildTikTokManageLink],
  )
  const accountApiTotals: AnalyticsTotals | null = useMemo(() => {
    const raw = accountQuery.data?.totals
    if (!raw || typeof raw !== "object") return null
    if (Object.keys(raw).length === 0) return null
    return raw as AnalyticsTotals
  }, [accountQuery.data])

  const accountFiltered = useMemo(() => {
    let rows = accountRows
    const selectedBuyerIdSet = new Set(state.selectedBuyers)
    const selectedBuyerLabelSet = new Set(
      buyerRows
        .filter((r) => selectedBuyerIdSet.has(r.user_id))
        .map((r) => (r.username || "").trim().toLowerCase())
        .filter(Boolean),
    )

    if (selectedBuyerIdSet.size > 0) {
      rows = rows.filter((r) => {
        const rowMeta = r as unknown as Record<string, unknown>
        const idCandidates = [
          rowMeta.user_id,
          rowMeta.owner_id,
          rowMeta.media_buyer_id,
        ]
          .map((v) => Number(v))
          .filter((v) => Number.isFinite(v))
        if (idCandidates.length > 0) {
          return idCandidates.some((id) => selectedBuyerIdSet.has(id))
        }

        const textCandidates = [
          rowMeta.username,
          rowMeta.user_name,
          rowMeta.buyer_username,
          rowMeta.media_buyer,
          r.account_name,
          r.advertiser_id,
        ]
          .map((v) => (v == null ? "" : String(v)).toLowerCase())
          .filter(Boolean)

        if (selectedBuyerLabelSet.size === 0) return true
        return Array.from(selectedBuyerLabelSet).some((buyerLabel) =>
          textCandidates.some((txt) => txt.includes(buyerLabel)),
        )
      })
    }

    if (state.selectedNetworks.length > 0) {
      const selectedSet = new Set(state.selectedNetworks.map((v) => v.toLowerCase()))
      rows = rows.filter((r) =>
        selectedSet.has((r.network || "").toLowerCase()),
      )
    }
    const q = state.search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const name = (r.account_name || r.advertiser_id || "").toLowerCase()
      const net = (r.network || "").toLowerCase()
      const advId = (r.advertiser_id || "").toLowerCase()
      const advStatus = (r.advertiser_status || "").toLowerCase()
      return (
        name.includes(q) ||
        net.includes(q) ||
        advId.includes(q) ||
        advStatus.includes(q)
      )
    })
  }, [accountRows, buyerRows, state.search, state.selectedNetworks, state.selectedBuyers])

  const accountSorted = useMemo(
    () =>
      sortRows(
        accountFiltered,
        state.accountSortKey,
        state.accountSortDir,
        DASHBOARD_ACCOUNT_COLUMNS.find((c) => c.id === state.accountSortKey)
          ?.type ?? "number",
      ),
    [accountFiltered, state.accountSortKey, state.accountSortDir],
  )

  const accountPageRows = useMemo(() => {
    const start = (state.accountPage - 1) * DASHBOARD_PAGE_SIZE
    return accountSorted.slice(start, start + DASHBOARD_PAGE_SIZE)
  }, [accountSorted, state.accountPage])

  const accountTotals: AnalyticsTotals | null = useMemo(() => {
    if (
      state.search.trim() ||
      state.selectedNetworks.length > 0 ||
      state.selectedBuyers.length > 0
    ) {
      return _computeTotalsFromAccountRows(accountFiltered)
    }
    return accountApiTotals
  }, [
    state.search,
    state.selectedNetworks,
    state.selectedBuyers,
    accountFiltered,
    accountApiTotals,
  ])

  // --------- Column reorder handlers ------------------------------------
  const onBuyerColumnOrderChange = (order: string[]) => {
    dispatch({ type: "SET_BUYER_COLUMN_ORDER", order })
    saveColumnOrder(DASHBOARD_BUYER_COLUMN_STORAGE_KEY, order)
  }
  const onAccountColumnOrderChange = (order: string[]) => {
    dispatch({ type: "SET_ACCOUNT_COLUMN_ORDER", order })
    saveColumnOrder(DASHBOARD_ACCOUNT_COLUMN_STORAGE_KEY, order)
  }

  // --------- Filter handlers --------------------------------------------
  const onPresetChange = (preset: DashboardDatePreset) => {
    runPreset(preset, (p, start, end, label) =>
      dispatch({
        type: "SET_PRESET",
        preset: p,
        startDate: start,
        endDate: end,
        displayLabel: label,
      }),
    )
  }

  const onCustomRangeApply = (
    startIso: string,
    endIso: string,
    displayLabel: string,
  ) => {
    dispatch({
      type: "SET_CUSTOM_RANGE",
      startDate: startIso,
      endDate: endIso,
      displayLabel,
    })
  }

  const onRefresh = () => {
    if (state.activeTab === "buyer") buyerQuery.refetch()
    else accountQuery.refetch()
  }

  const activeLoading =
    state.activeTab === "buyer"
      ? buyerQuery.isFetching && !buyerQuery.data
      : accountQuery.isFetching && !accountQuery.data

  const navbarTabs = useMemo(
    () => (
      <NavbarTabList>
        {(
          [
            { value: "buyer", label: "By Media Buyer" },
            { value: "account", label: "By Ad Account" },
          ] as const
        ).map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => dispatch({ type: "SET_TAB", tab: t.value })}
            data-tab={t.value}
            className={navbarTabClass(state.activeTab === t.value)}
          >
            {t.label}
          </button>
        ))}
      </NavbarTabList>
    ),
    [state.activeTab],
  )
  useNavbarLeftAction(navbarTabs)

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[#2d2d2d]">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-1.5 pt-1.5 sm:px-2 sm:pt-2">
        {tiktokConnected ? (
          <Alert className="mb-2 shrink-0 border-[#2d5f47] bg-[#1e4d3e] text-[#4ade80]">
            <AlertDescription>
              {"\u2705 TikTok account connected successfully!"}
            </AlertDescription>
          </Alert>
        ) : null}

        {errorMessage ? (
          <Alert
            variant="destructive"
            className="mb-2 shrink-0 border-[#663333] bg-[#4a2a2a] text-[#f87171]"
          >
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}

        <div className="shrink-0">
          <DashboardFilters
            state={state}
            onNetworksChange={(values) =>
              dispatch({ type: "SET_NETWORKS", values })
            }
            onBuyerStatusesChange={(values) =>
              dispatch({ type: "SET_BUYER_STATUSES", values })
            }
            onAccountStatusesChange={(values) =>
              dispatch({ type: "SET_ACCOUNT_STATUSES", values })
            }
            onBuyersChange={(ids) => dispatch({ type: "SET_BUYERS", ids })}
            onSearchChange={(v) => dispatch({ type: "SET_SEARCH", value: v })}
            onRefresh={onRefresh}
            onPresetChange={onPresetChange}
            onCustomRangeApply={onCustomRangeApply}
            buyerOptions={buyerOptions}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {state.activeTab === "buyer" ? (
            <BuyerAnalyticsTable
              rows={buyerPageRows}
              totals={buyerTotals}
              columnOrder={state.buyerColumnOrder}
              sortKey={state.buyerSortKey}
              sortDir={state.buyerSortDir}
              onSort={(key, columnType) =>
                dispatch({ type: "SET_BUYER_SORT", key, columnType })
              }
              onColumnOrderChange={onBuyerColumnOrderChange}
              startDate={state.startDate}
              endDate={state.endDate}
              network={
                state.selectedNetworks.length === 1
                  ? state.selectedNetworks[0]
                  : "all"
              }
              isLoading={activeLoading}
            />
          ) : (
            <AccountAnalyticsTable
              rows={accountPageRows}
              totals={accountTotals}
              columnOrder={state.accountColumnOrder}
              sortKey={state.accountSortKey}
              sortDir={state.accountSortDir}
              onSort={(key, columnType) =>
                dispatch({ type: "SET_ACCOUNT_SORT", key, columnType })
              }
              onColumnOrderChange={onAccountColumnOrderChange}
              startDate={state.startDate}
              endDate={state.endDate}
              network={
                state.selectedNetworks.length === 1
                  ? state.selectedNetworks[0]
                  : "all"
              }
              isLoading={activeLoading}
            />
          )}
        </div>
      </div>

      {state.activeTab === "buyer" ? (
        <Pagination
          className="border-t border-[#4a4a4a] bg-[#303030] px-2 py-2 sm:px-3"
          page={state.buyerPage}
          total={buyerSorted.length}
          pageSize={DASHBOARD_PAGE_SIZE}
          onPageChange={(page) => dispatch({ type: "SET_BUYER_PAGE", page })}
        />
      ) : (
        <Pagination
          className="border-t border-[#4a4a4a] bg-[#303030] px-2 py-2 sm:px-3"
          page={state.accountPage}
          total={accountSorted.length}
          pageSize={DASHBOARD_PAGE_SIZE}
          onPageChange={(page) =>
            dispatch({ type: "SET_ACCOUNT_PAGE", page })
          }
        />
      )}
    </div>
  )
}

/**
 * Shared row sort — numeric subtraction for numbers, case-insensitive
 * `localeCompare` for strings. Parity: `_dashboardSortRows`
 * (dashboard.html:1475-1492).
 */
function sortRows<Row>(
  rows: Row[],
  sortKey: string,
  sortDir: "asc" | "desc",
  columnType: "string" | "number",
): Row[] {
  const mult = sortDir === "asc" ? 1 : -1
  const arr = rows.slice()
  arr.sort((a, b) => {
    const rawA = (a as Record<string, unknown>)[sortKey]
    const rawB = (b as Record<string, unknown>)[sortKey]
    if (columnType === "number") {
      const va = Number(rawA)
      const vb = Number(rawB)
      const safeA = Number.isFinite(va) ? va : 0
      const safeB = Number.isFinite(vb) ? vb : 0
      if (safeA !== safeB) return mult * (safeA - safeB)
      return 0
    }
    const sa = (rawA == null ? "" : String(rawA)).toLowerCase()
    const sb = (rawB == null ? "" : String(rawB)).toLowerCase()
    return mult * sa.localeCompare(sb)
  })
  return arr
}
